/**
 * task-threads-reducer.ts — Redux reducer for Spec 017 Universal Task Thread UI Sync.
 *
 * Implements equa-web live-sync invariants INV-001..INV-011 from
 * contracts/reducer-contract.md. INV-012 is scoped to offline-capable native
 * and extension stores.
 * See specs/017-universal-task-thread-ui-sync/contracts/reducer-contract.md.
 *
 * Spec 017 FR-007, FR-008, FR-009, FR-010, FR-011, FR-013.
 */

import { AnyAction, Reducer } from 'redux'
import {
  LiveEvent,
  TaskThreadItem,
  TaskThreadsState,
  IssueShape,
} from '@logic/types/task-thread'

const APPLIED_DEDUP_KEY_LIMIT = 512

// ---------------------------------------------------------------------------
// Action Types
// ---------------------------------------------------------------------------

export const TASK_THREADS = {
  /** REST hydrate response received — authoritative list snapshot */
  HYDRATE: 'task_threads/HYDRATE',
  /** Single task fetched (e.g. after UNKNOWN-TASK refetch trigger) */
  HYDRATE_ONE: 'task_threads/HYDRATE_ONE',
  /** LiveEvent received from WS */
  EVENT: 'task_threads/EVENT',
  /** WS close code 4040 — full_refetch_required */
  FULL_REFETCH: 'task_threads/FULL_REFETCH',
  /** Surface status change */
  SET_STATUS: 'task_threads/SET_STATUS',
  /** Worktree registration returned */
  SET_WORKTREE: 'task_threads/SET_WORKTREE',
  /** Set subscribed companyId */
  SET_COMPANY: 'task_threads/SET_COMPANY',
  /** Optimistic update (UI affordance only — server echo MUST override, INV-009) */
  OPTIMISTIC_UPDATE: 'task_threads/OPTIMISTIC_UPDATE',
} as const

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export const initialTaskThreadsState: TaskThreadsState = {
  items: {},
  order: [],
  lastEventId: null,
  status: 'idle',
  error: null,
  companyId: null,
  worktreeId: null,
  pendingEventBuffer: [],
  appliedDedupKeys: [],
}

// ---------------------------------------------------------------------------
// Pure reducer helpers
// ---------------------------------------------------------------------------

/** Build a TaskThreadItem from a partial IssueShape event payload */
function toItem(shape: Partial<IssueShape> & { id: string }): TaskThreadItem {
  return {
    id: shape.id,
    companyId: shape.companyId ?? '',
    title: shape.title ?? '',
    description: shape.description,
    status: shape.status ?? 'pending',
    priority: shape.priority ?? 'medium',
    assigneeId: shape.assigneeId ?? null,
    updatedAt: shape.updatedAt ?? shape.createdAt ?? '',
    summary: shape.summary ?? shape.description,
    resources: shape.resources,
    conversation: shape.conversation,
  }
}

function hasPatchField<K extends keyof IssueShape>(
  patch: Partial<IssueShape>,
  key: K
): boolean {
  return Object.prototype.hasOwnProperty.call(patch, key)
}

function mergeItem(
  existing: TaskThreadItem,
  patch: Partial<IssueShape> & { id: string }
): TaskThreadItem {
  const hasDescription = hasPatchField(patch, 'description')
  const hasSummary = hasPatchField(patch, 'summary')
  let summary = existing.summary
  if (hasDescription) summary = patch.description
  if (hasSummary) summary = patch.summary

  return {
    ...existing,
    companyId: patch.companyId ?? existing.companyId,
    title: patch.title ?? existing.title,
    description: hasDescription ? patch.description : existing.description,
    status: patch.status ?? existing.status,
    priority: patch.priority ?? existing.priority,
    assigneeId: hasPatchField(patch, 'assigneeId')
      ? patch.assigneeId ?? null
      : existing.assigneeId,
    updatedAt: patch.updatedAt ?? existing.updatedAt,
    summary,
    resources: hasPatchField(patch, 'resources')
      ? patch.resources
      : existing.resources,
    conversation: hasPatchField(patch, 'conversation')
      ? patch.conversation
      : existing.conversation,
  }
}

/**
 * Apply a single LiveEvent to state.
 * Returns null if the event should be dropped (INV-001, INV-002, INV-010).
 * Returns { state, needsRefetch } if the event implies a REST refetch for unknown task (INV-004).
 */
function applyEvent(
  state: TaskThreadsState,
  event: LiveEvent
): { nextState: TaskThreadsState; needsRefetch: string | null } | null {
  // INV-010 — Company isolation
  if (event.companyId !== state.companyId) return null

  // INV-002 — Monotonic application order: drop stale events
  if (
    state.lastEventId !== null &&
    event.sequenceId <= state.lastEventId
  ) {
    return null
  }

  // INV-001 — Idempotence: dedup on (taskId, sequenceId)
  const dedupKey = `${event.task.id}:${event.sequenceId}`
  if (state.appliedDedupKeys.includes(dedupKey)) return null

  const taskId = event.task.id
  const nextApplied = [...state.appliedDedupKeys, dedupKey].slice(-APPLIED_DEDUP_KEY_LIMIT)

  const nextItems = { ...state.items }
  let nextOrder = [...state.order]
  let needsRefetch: string | null = null

  switch (event.type) {
    case 'task.created': {
      // INV-003 — upsert (REST-hydrated item or duplicate create is replaced with event payload)
      nextItems[taskId] = toItem(event.task as any)
      if (!nextOrder.includes(taskId)) {
        nextOrder = [...nextOrder, taskId]
      }
      break
    }

    case 'task.status_changed': {
      if (!nextItems[taskId]) {
        // INV-004 — unknown task: trigger refetch, buffer event
        needsRefetch = taskId
        // Return current state unchanged but flag refetch needed
        return {
          nextState: {
            ...state,
            pendingEventBuffer: [...state.pendingEventBuffer, event],
          },
          needsRefetch,
        }
      }
      // INV-009 — server event wins over optimistic write
      nextItems[taskId] = mergeItem(nextItems[taskId], event.task)
      break
    }

    case 'task.archived': {
      // INV-005 — set status to archived, REMOVE from active list (order)
      if (nextItems[taskId]) {
        nextItems[taskId] = {
          ...nextItems[taskId],
          status: 'archived',
          updatedAt: event.task.updatedAt ?? nextItems[taskId].updatedAt,
        }
      } else {
        nextItems[taskId] = toItem({ ...event.task, status: 'archived' } as any)
      }
      // Remove from active order (INV-005)
      nextOrder = nextOrder.filter(id => id !== taskId)
      break
    }

    case 'task.worktree_added':
    case 'task.worktree_removed': {
      // INV-006 — update worktrees array if detail loaded; otherwise no-op at list level
      if (nextItems[taskId] && event.worktree) {
        const existing = nextItems[taskId].worktrees ?? []
        if (event.type === 'task.worktree_added') {
          const alreadyPresent = existing.some(w => w.id === event.worktree!.id)
          nextItems[taskId] = {
            ...nextItems[taskId],
            worktrees: alreadyPresent ? existing : [...existing, event.worktree],
          }
        } else {
          nextItems[taskId] = {
            ...nextItems[taskId],
            worktrees: existing.map(w =>
              w.id === event.worktree!.id
                ? { ...w, detachedAt: event.worktree!.detachedAt }
                : w
            ),
          }
        }
      }
      break
    }
  }

  return {
    nextState: {
      ...state,
      items: nextItems,
      order: nextOrder,
      lastEventId: event.sequenceId,   // INV-011
      appliedDedupKeys: nextApplied,
    },
    needsRefetch,
  }
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export const taskThreadsReducer: Reducer<TaskThreadsState, AnyAction> = (
  state = initialTaskThreadsState,
  action
) => {
  switch (action.type) {
    // ── SET_COMPANY ────────────────────────────────────────────────────────
    case TASK_THREADS.SET_COMPANY: {
      if (state.companyId === action.companyId) return state
      return {
        ...initialTaskThreadsState,
        companyId: action.companyId,
        status: 'idle',
      }
    }

    // ── HYDRATE ────────────────────────────────────────────────────────────
    case TASK_THREADS.HYDRATE: {
      // INV-008 — REST hydrate is authoritative for list ordering and content
      const items: Record<string, TaskThreadItem> = {}
      const order: string[] = []
      for (const issue of (action.issues as IssueShape[])) {
        items[issue.id] = toItem(issue)
        if (issue.status !== 'archived') {
          order.push(issue.id)
        }
      }
      // After hydrate, flush any buffered events (INV-008)
      let nextState: TaskThreadsState = {
        ...state,
        items,
        order,
        status: 'live',
        error: null,
        lastEventId: action.lastEventId ?? state.lastEventId,
        pendingEventBuffer: [],
        appliedDedupKeys: [],
      }
      // Apply buffered events in sequence
      for (const buffered of state.pendingEventBuffer) {
        const result = applyEvent(nextState, buffered)
        if (result) nextState = result.nextState
      }
      return nextState
    }

    // ── HYDRATE_ONE ────────────────────────────────────────────────────────
    case TASK_THREADS.HYDRATE_ONE: {
      const issue = action.issue as IssueShape
      const nextItems = { ...state.items, [issue.id]: toItem(issue) }
      const nextOrder =
        issue.status !== 'archived' && !state.order.includes(issue.id)
          ? [...state.order, issue.id]
          : state.order
      let nextState: TaskThreadsState = {
        ...state,
        items: nextItems,
        order: nextOrder,
      }
      // Flush any buffered events referencing this task
      const remainingBuffer: LiveEvent[] = []
      for (const buffered of state.pendingEventBuffer) {
        if (buffered.task.id === issue.id) {
          const result = applyEvent(nextState, buffered)
          if (result) nextState = result.nextState
        } else {
          remainingBuffer.push(buffered)
        }
      }
      return { ...nextState, pendingEventBuffer: remainingBuffer }
    }

    // ── EVENT ──────────────────────────────────────────────────────────────
    case TASK_THREADS.EVENT: {
      const event = action.event as LiveEvent
      // If hydrate is in-flight, buffer the event (INV-008)
      if (state.status === 'loading') {
        return {
          ...state,
          pendingEventBuffer: [...state.pendingEventBuffer, event],
        }
      }
      const result = applyEvent(state, event)
      if (!result) return state   // dropped
      return result.nextState
    }

    // ── FULL_REFETCH ───────────────────────────────────────────────────────
    case TASK_THREADS.FULL_REFETCH: {
      // INV-007 — reset all task state, clear cursor, set loading
      return {
        ...state,
        items: {},
        order: [],
        lastEventId: null,
        status: 'loading',
        error: null,
        pendingEventBuffer: [],
        appliedDedupKeys: [],
      }
    }

    // ── SET_STATUS ─────────────────────────────────────────────────────────
    case TASK_THREADS.SET_STATUS: {
      return { ...state, status: action.status, error: action.error ?? null }
    }

    // ── SET_WORKTREE ───────────────────────────────────────────────────────
    case TASK_THREADS.SET_WORKTREE: {
      return { ...state, worktreeId: action.worktreeId }
    }

    // ── OPTIMISTIC_UPDATE ──────────────────────────────────────────────────
    case TASK_THREADS.OPTIMISTIC_UPDATE: {
      // INV-009 — optimistic write is UI-only affordance; server echo MUST override
      const taskId = action.taskId as string
      if (!state.items[taskId]) return state
      return {
        ...state,
        items: {
          ...state.items,
          [taskId]: { ...state.items[taskId], ...action.patch },
        },
      }
    }

    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Action creators
// ---------------------------------------------------------------------------

export const taskThreadsActions = {
  setCompany: (companyId: string) => ({
    type: TASK_THREADS.SET_COMPANY,
    companyId,
  }),
  hydrate: (issues: IssueShape[], lastEventId?: string | null) => ({
    type: TASK_THREADS.HYDRATE,
    issues,
    lastEventId,
  }),
  hydrateOne: (issue: IssueShape) => ({
    type: TASK_THREADS.HYDRATE_ONE,
    issue,
  }),
  event: (event: LiveEvent) => ({
    type: TASK_THREADS.EVENT,
    event,
  }),
  fullRefetch: () => ({
    type: TASK_THREADS.FULL_REFETCH,
  }),
  setStatus: (
    status: TaskThreadsState['status'],
    error?: string | null
  ) => ({
    type: TASK_THREADS.SET_STATUS,
    status,
    error,
  }),
  setWorktree: (worktreeId: string | null) => ({
    type: TASK_THREADS.SET_WORKTREE,
    worktreeId,
  }),
  optimisticUpdate: (
    taskId: string,
    patch: Partial<TaskThreadItem>
  ) => ({
    type: TASK_THREADS.OPTIMISTIC_UPDATE,
    taskId,
    patch,
  }),
} as const
